import { motion } from "framer-motion";
import { useMemo } from "react";
import { UserAnalytics } from "@/components/users/UserAnalytics";
import { UsersExplorer } from "@/components/users/UsersExplorer";
import { PageHeader } from "@/components/ui/PageHeader";
import { ExplorerSkeleton } from "@/components/ui/PageSkeleton";
import { useUserPlatformData } from "@/lib/queries";
import { enrichUsers } from "@/lib/user-analytics";

const pageVariants = {
  initial: { opacity: 0, y: 8 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -4 },
};

export function UsersPage() {
  const { data, isLoading, error } = useUserPlatformData();

  const users = useMemo(() => {
    if (!data) return [];
    return enrichUsers(data.users, data.cars, data.serviceHistory, data.shftServices);
  }, [data]);

  return (
    <motion.div
      variants={pageVariants}
      initial="initial"
      animate="animate"
      exit="exit"
      transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
      className="space-y-6"
    >
      <PageHeader
        title="Users"
        description="Browse platform members, review ownership and service activity, and open any profile for full detail."
        showGreeting={false}
      />

      {isLoading ? (
        <div className="panel overflow-hidden rounded-3xl p-4 sm:p-5">
          <div className="mb-5 h-6 w-40 animate-pulse rounded-full bg-border/50" />
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {Array.from({ length: 4 }).map((_, index) => (
              <div key={index} className="h-24 animate-pulse rounded-2xl bg-border/40" />
            ))}
          </div>
        </div>
      ) : error || !data ? (
        <p className="text-sm text-red-600">Failed to load user analytics.</p>
      ) : (
        <UserAnalytics
          users={data.users}
          cars={data.cars}
          serviceHistory={data.serviceHistory}
          shftServices={data.shftServices}
        />
      )}

      {isLoading ? (
        <ExplorerSkeleton title="Users" />
      ) : error ? (
        <p className="text-sm text-red-600">Failed to load users. Please try again.</p>
      ) : (
        <UsersExplorer users={users} />
      )}
    </motion.div>
  );
}
